import {
  CaretLeftOutlined,
  DownloadOutlined,
  HistoryOutlined,
  CloudDownloadOutlined,
  CalendarOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import {
  message,
  Button,
  Popover,
  Tooltip,
  Empty,
  Form,
  InputNumber,
  Select,
  Spin,
  Timeline,
} from 'antd';
import React, { useState, useEffect } from 'react';
import { useAsync, useToggle } from 'react-use';
import { queryClient } from '../../QueryClient';
import { fetchAPI, downloadFile } from '../../utils/api';
import styled from 'styled-components';
import { REGIONS } from '../../constants/REGIONS';
import { formatUTCTimeToLocal } from '../../utils/format';

const COUNTRY_CODES = [
  'SG',
  'MY',
  'TH',
  'VN',
  'ID',
  'IN',
  'AU',
  'CN',
  'JP',
  'HK',
  'PH',
];

const ChangeRateWizard: React.FC<{
  recordId: number;
  po_reference_number: string;
  onOk: () => void;
}> = props => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const currencies = Array.from(new Set(Object.values(REGIONS).sort()));
  const currencies_default = useAsync(async () => {
    try {
      const { rate } = await fetchAPI('exchange-rates/latest');
      return rate;
    } catch (error) {
      message.error('Failed get default currencies');
      return [];
    }
  }, []);
  const onFinishChangeRate = async (values: any) => {
    setLoading(true);
    try {
      const blob = await fetchAPI('order/gen_invoice', {
        payload: {
          id: props.recordId,
          manual_rate: {
            exchange_rate: values.exchange_rate,
            to_currency: values.to_currency,
          },
          enforce_region_config: values.region_conf, // new
        },
        method: 'POST',
      });
      downloadFile(
        blob,
        `INVOICE_${props.recordId}_${props.po_reference_number}`
      );
    } catch (error) {
      message.error('Failed download invoice with manual exchange rates');
    } finally {
      setLoading(false);
      props.onOk();
    }
  };
  const onCurrencyChange = (value: string) => {
    form.setFieldsValue({
      to_currency: value,
      exchange_rate: currencies_default.value[value],
    });
  };
  return (
    <Spin spinning={loading}>
      <Form
        form={form}
        name="wizard-manual-rate"
        labelCol={{ span: 12 }}
        wrapperCol={{ span: 16 }}
        style={{ maxWidth: 600 }}
        initialValues={{ remember: true }}
        onFinish={onFinishChangeRate}
        autoComplete="off"
      >
        <Form.Item
          label="Currency"
          name="to_currency"
          rules={[{ required: false }]}
        >
          <Spin spinning={currencies_default.loading}>
            <Select placeholder="--" onChange={onCurrencyChange} allowClear>
              {currencies.map(value => (
                <Select.Option key={value} value={value}>
                  {value}
                </Select.Option>
              ))}
            </Select>
          </Spin>
        </Form.Item>

        <Form.Item
          label="Fx. Rate"
          name="exchange_rate"
          rules={[{ required: false }]}
        >
          <InputNumber />
        </Form.Item>

        <Form.Item
          label="Region Config"
          name="region_conf"
          rules={[{ required: false }]}
        >
          <Select placeholder="--" allowClear>
            {COUNTRY_CODES.map(code => (
              <Select.Option key={code} value={code}>
                {code}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item wrapperCol={{ offset: 8, span: 16 }}>
          <Button type="primary" htmlType="submit">
            Download
          </Button>
        </Form.Item>
      </Form>
    </Spin>
  );
};

const ChangeAddressWizard: React.FC<{
  recordId: number;
  po_reference_number: string;
  defaultAddress: string;
  defaultEntity: string;
  defaultPhone: string;
  onOk: () => void;
}> = props => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [rawAddressList, setRawAddressList] = useState([]);
  const [selectedEntity, setSelectedEntity] = useState('');
  const [selectedPhone, setSelectedPhone] = useState('');
  const [selectedAddress, setSelectedAddress] = useState('');
  const addressList = useAsync(async (): Promise<{
    nameList: any[];
    phoneList: any[];
    addressList: any[];
  }> => {
    try {
      const { items } = await fetchAPI('entity/invoice_address');
      const cleanItems = items.filter(row => {
        return !!row.default_billing_address;
      });
      setRawAddressList(cleanItems);
      const uniqueNames = Array.from(
        new Set([...cleanItems.map(i => i.name), props.defaultEntity])
      );
      const uniquePhones = Array.from(
        new Set([
          ...cleanItems.map(i => i.default_contact_number),
          props.defaultPhone,
        ])
      );
      const uniqueAddresses = Array.from(
        new Set([
          ...cleanItems.map(i => i.default_billing_address),
          props.defaultAddress,
        ])
      );
      return {
        nameList: uniqueNames,
        phoneList: uniquePhones,
        addressList: uniqueAddresses,
      };
    } catch (error) {
      message.error('Failed get address list');
      return { nameList: [], phoneList: [], addressList: [] };
    }
  }, []);
  const onFinishChangeAddress = async () => {
    setLoading(true);
    try {
      const blob = await fetchAPI('order/gen_invoice', {
        payload: {
          id: props.recordId,
          enforce_invoice_address: selectedAddress,
          enforce_entity_name: selectedEntity,
          enforce_phone: selectedPhone,
        },
        method: 'POST',
      });
      downloadFile(
        blob,
        `INVOICE_${props.recordId}_${props.po_reference_number}`
      );
    } catch (error) {
      message.error('Failed download invoice with selected address');
    } finally {
      setLoading(false);
      props.onOk();
    }
  };
  const onAddressChange = (value: string) => {
    setSelectedAddress(value);
  };
  const onEntityChange = (value: string) => {
    setSelectedEntity(value);
    const selectedRow = rawAddressList.filter(row => row.name === value);
    if (!!selectedRow.length) {
      setSelectedPhone(selectedRow[0].default_contact_number);
      setSelectedAddress(selectedRow[0].default_billing_address);
    }
  };
  const onPhoneChange = (value: string) => {
    setSelectedPhone(value);
  };

  useEffect(() => {
    setSelectedPhone(props.defaultPhone);
    setSelectedAddress(props.defaultAddress);
    setSelectedEntity(props.defaultEntity);
  }, [props.recordId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Spin spinning={loading}>
      <Form
        form={form}
        name="wizard-select-entity"
        labelCol={{ span: 4 }}
        wrapperCol={{ span: 16 }}
        style={{ minWidth: 600 }}
        initialValues={{ remember: true }}
        onFinish={onFinishChangeAddress}
        autoComplete="off"
      >
        <Form.Item label="Entity Name" name="selected_entity">
          <Spin spinning={addressList.loading}>
            <Select
              defaultValue={props.defaultEntity}
              placeholder="--"
              onChange={e => onEntityChange(e.toString())}
              allowClear
            >
              {(addressList?.value?.nameList || []).map(val => (
                <Select.Option key={val} value={val}>
                  {val}
                </Select.Option>
              ))}
            </Select>
          </Spin>
        </Form.Item>
        <Form.Item label="Address" name="selected_address">
          <Spin spinning={addressList.loading}>
            <Select
              value={selectedAddress}
              defaultValue={props.defaultAddress}
              placeholder="--"
              onChange={e => onAddressChange(e.toString())}
              allowClear
            >
              {(addressList?.value?.addressList || []).map(val => (
                <Select.Option key={val} value={val}>
                  {val}
                </Select.Option>
              ))}
            </Select>
          </Spin>
        </Form.Item>
        <Form.Item label="Phone Number" name="selected_phone_number">
          <Spin spinning={addressList.loading}>
            <Select
              value={selectedPhone}
              defaultValue={props.defaultPhone}
              placeholder="--"
              onChange={e => onPhoneChange(e.toString())}
              allowClear
            >
              {(addressList?.value?.phoneList || []).map(val => (
                <Select.Option key={val} value={val}>
                  {val}
                </Select.Option>
              ))}
            </Select>
          </Spin>
        </Form.Item>

        <Form.Item wrapperCol={{ offset: 8, span: 16 }}>
          <Button type="primary" htmlType="submit">
            Download
          </Button>
        </Form.Item>
      </Form>
    </Spin>
  );
};

const DownloadInvoiceHistory: React.FC<{ record; popoverContent }> = props => {
  const history = useQuery(
    [
      'download-order-invoice-history',
      props.record.id,
      props.popoverContent === 'history',
    ],
    async () => {
      if (props.popoverContent !== 'history') return [];
      try {
        const resp = await fetchAPI('user/activity', {
          payload: {
            table_name: 'order',
            value_type: 'generate_invoice',
            row_id: props.record.id,
          },
        });
        return resp.items || [];
      } catch {
        return [];
      }
    },
    { staleTime: 5 * 60 * 1000 }
  );

  return (
    <StatusTimeline>
      <Spin spinning={history.isLoading}>
        <Timeline>
          {(history?.data || []).map(status => (
            <Timeline.Item key={status?.id} dot={<CalendarOutlined />}>
              Download Invoice
              <span
                style={{ display: 'block', fontSize: '11px', opacity: '0.7' }}
              >
                {formatUTCTimeToLocal(status?.created_at, 'DD MMM YYYY HH:mm')}
                <div style={{ fontSize: '11px' }}>
                  <UserOutlined /> By {status?.user?.full_name}
                </div>
              </span>
            </Timeline.Item>
          ))}
        </Timeline>
        {!history?.data?.length && (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
        )}
      </Spin>
    </StatusTimeline>
  );
};

const MenuDownloadInvoice = ({
  record,
  setPopoverVisible,
  setTitleComponent,
  modalComponent,
  setModalComponent,
  popoverContent,
  setPopoverContent,
  loading,
  toggleLoading,
}) => {
  const [isGroupInvoiceAddres, setIsGroupInvoiceAddress] = useState(false);
  const [defaultAddress, setDefaultAddress] = useState('');
  const [defaultEntity, setDefaultEntity] = useState('');
  const [defaultPhone, setDefaultPhone] = useState('');
  const setTitle = () => {
    if (popoverContent === 'menu') {
      setTitleComponent('Download Invoice');
    } else if (popoverContent === 'history') {
      setTitleComponent(
        <span
          onClick={() => setPopoverContent('menu')}
          style={{ cursor: 'pointer' }}
        >
          <CaretLeftOutlined /> History Download Invoice
        </span>
      );
    } else if (popoverContent === 'change-rate') {
      setTitleComponent(
        <span
          onClick={() => setPopoverContent('menu')}
          style={{ cursor: 'pointer' }}
        >
          <CaretLeftOutlined /> Download and change rate
        </span>
      );
    } else if (popoverContent === 'invoice-address') {
      setTitleComponent(
        <span
          onClick={() => setPopoverContent('menu')}
          style={{ cursor: 'pointer' }}
        >
          <CaretLeftOutlined /> Download and change address
        </span>
      );
    }
  };
  const getOrderPreDownlaodInvoice = async (id: number) => {
    try {
      const resp = await fetchAPI(`order/pre-download-invoice`, {
        payload: { id },
        method: 'post',
      });
      setIsGroupInvoiceAddress(resp.is_group_as_inv_address);
      setDefaultAddress(resp.address);
      setDefaultEntity(resp.entity_name);
      setDefaultPhone(resp.buyer_phone_number);
    } catch {
      message.error('Failed get order pre download invoice');
    }
  };
  useEffect(() => {
    if (record.id) {
      getOrderPreDownlaodInvoice(record.id);
    } else {
      setIsGroupInvoiceAddress(false);
      setDefaultAddress('');
      setDefaultEntity('');
      setDefaultPhone('');
    }
  }, [record.id]);
  const setModal = () => {
    if (popoverContent === 'menu') {
      setModalComponent(
        <>
          {isGroupInvoiceAddres ? (
            <Button
              icon={<DownloadOutlined />}
              type="link"
              loading={loading}
              block
              onClick={() => setPopoverContent('invoice-address')}
              style={{ textAlign: 'left' }}
            >
              Download and change address
            </Button>
          ) : (
            <Button
              icon={<DownloadOutlined />}
              type="link"
              loading={loading}
              block
              onClick={() => doDownloadInvoice()}
              style={{ textAlign: 'left' }}
            >
              Download
            </Button>
          )}
          <Button
            icon={<HistoryOutlined />}
            type="link"
            onClick={() => setPopoverContent('history')}
            block
            style={{ textAlign: 'left' }}
          >
            History
          </Button>
          <Button
            icon={<CloudDownloadOutlined />}
            type="link"
            loading={loading}
            block
            onClick={() => setPopoverContent('change-rate')}
            style={{ textAlign: 'left' }}
          >
            Download and change rate
          </Button>
        </>
      );
    } else if (popoverContent === 'history') {
      setModalComponent(
        <DownloadInvoiceHistory
          record={record}
          popoverContent={popoverContent}
        />
      );
    } else if (popoverContent === 'change-rate') {
      setModalComponent(
        <ChangeRateWizard
          recordId={record.id}
          po_reference_number={record?.po_reference_number}
          onOk={() => {
            setPopoverVisible(false);
          }}
        />
      );
    } else if (popoverContent === 'invoice-address') {
      setModalComponent(
        <ChangeAddressWizard
          recordId={record.id}
          po_reference_number={record?.po_reference_number}
          defaultAddress={defaultAddress}
          defaultEntity={defaultEntity}
          defaultPhone={defaultPhone}
          onOk={() => {
            setPopoverVisible(false);
          }}
        />
      );
    }
  };

  useEffect(() => {
    setTitle();
    setModal();
  }, [popoverContent, isGroupInvoiceAddres]); // eslint-disable-line react-hooks/exhaustive-deps

  const doDownloadInvoice = async () => {
    toggleLoading();
    const blob = await fetchAPI('order/gen_invoice', {
      payload: { id: record.id },
      method: 'POST',
    });
    downloadFile(
      blob,
      `${record.po_reference_number}_${record.assignee_region_identifier}`
    );
    toggleLoading();
    setPopoverVisible(false);
    queryClient.invalidateQueries({
      queryKey: ['download-order-invoice-history', record.id, true],
    });
  };
  return <Spin spinning={loading}>{modalComponent}</Spin>;
};

const DownloadInvoice = ({
  record,
  onOpenChange,
}: {
  record: any;
  onOpenChange?: (open: boolean) => void;
}) => {
  const [loading, toggleLoading] = useToggle(false);
  const [popoverVisible, setPopoverVisible] = useState(false);
  const [titleComponent, setTitleComponent] = useState<any>();
  const [modalComponent, setModalComponent] = useState<any>();
  const [popoverContent, setPopoverContent] = useState<
    'menu' | 'history' | 'change-rate' | 'invoice-address'
  >('menu');
  const title = 'Download Invoice';

  const handlePopoverClose = (vis: boolean) => {
    if (!vis && !loading) {
      setPopoverVisible(vis);
      onOpenChange?.(false);
    } else {
      setPopoverVisible(vis);
      setPopoverContent('menu');
    }
  };

  return (
    <Popover
      title={titleComponent}
      content={
        <MenuDownloadInvoice
          record={record}
          setPopoverVisible={setPopoverVisible}
          setTitleComponent={setTitleComponent}
          modalComponent={modalComponent}
          setModalComponent={setModalComponent}
          popoverContent={popoverContent}
          setPopoverContent={setPopoverContent}
          loading={loading}
          toggleLoading={toggleLoading}
        />
      }
      trigger="click"
      visible={popoverVisible}
      onVisibleChange={handlePopoverClose}
      placement="right"
    >
      <Tooltip title={title}>
        <Button
          loading={loading}
          icon={<DownloadOutlined />}
          onClick={() => {
            onOpenChange?.(false);
          }}
        >
          Download Invoice
        </Button>
      </Tooltip>
    </Popover>
  );
};

export default DownloadInvoice;

export const StatusTimeline = styled.div`
  .ant-timeline {
    margin-top: 15px;
  }
  .ant-timeline-item {
    font-size: 12px;
    &:last-child {
      padding-bottom: 0px;
    }
  }
`;
